import { AppShell } from '@mantine/core';
import { AnimatePresence } from 'motion/react';
import { ReactNode, useMemo } from 'react';
import { Link } from 'react-router-dom';

import eterno_logo from '@/assets/logo-with-name.svg';

import CustomerPracticeSelection from '@/components/CustomerPracticeSelection.tsx';
import InstanceLegend from '@/components/InstanceLegend.tsx';
import { HeaderNavigation } from '@/components/header-navigation.tsx';
import { UserMenu } from '@/components/user-menu.tsx';

import { AppRoute } from '@/constants/app-routes.ts';

import useConfig from '@/hooks/useConfig.ts';

import CustomerPrinterSelection from './CustomerPrinterSelection.tsx';

interface Props {
    children: ReactNode;
}

export const OfficeAppShell = ({ children }: Props) => {
    const { selected_location: selectedLocation, selected_customer: selectedCustomer } = useConfig();
    const showPrinter = useMemo(
        () =>
            selectedLocation.configs.some(
                (config: import('@/models/EnrichedPrismicDocuments').EnrichedDCConfig) =>
                    config.is_new_checkin_available && config.pcs_printers?.length > 0
            ),
        [selectedLocation]
    );

    return (
        <AnimatePresence mode="wait">
            <AppShell header={{ height: 60 }}>
                <AppShell.Header>
                    <div className="flex h-full items-center justify-between px-10">
                        <div className="flex h-full items-center gap-6">
                            <Link to={AppRoute.ROOT}>
                                <img
                                    src={selectedCustomer?.customer_icon_large?.url ?? eterno_logo}
                                    alt={selectedCustomer?.customer_name ?? 'eterno'}
                                    className="h-8 max-w-[10rem] object-contain dark:invert"
                                />
                            </Link>
                            <CustomerPracticeSelection />
                            {showPrinter && <CustomerPrinterSelection />}
                            <InstanceLegend />
                        </div>

                        <div className="flex items-center gap-8">
                            <HeaderNavigation />
                            <UserMenu />
                        </div>
                    </div>
                </AppShell.Header>

                <AppShell.Main
                    classNames={{
                        main: 'h-svh',
                    }}
                >
                    {children}
                </AppShell.Main>
            </AppShell>
        </AnimatePresence>
    );
};
